import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Trash2, Plus, Upload } from "lucide-react";
import "./pagebuilder.css";

/* ---------------------------------------------------------------------------
 * Inline WYSIWYG editable primitives
 * ------------------------------------------------------------------------- */

// Plain-text contentEditable element. Seeds its text once (via a ref callback)
// to avoid caret jumping, and commits on blur.
function EditableText({ as: Tag = "div", value, onChange, edit, className = "", placeholder = "" }) {
  if (!edit) return <Tag className={className}>{value || ""}</Tag>;
  const seed = (el) => {
    if (el && el.dataset.seeded !== "1") {
      el.innerText = value || "";
      el.dataset.seeded = "1";
    }
  };
  return (
    <Tag
      ref={seed}
      contentEditable
      suppressContentEditableWarning
      data-ph={placeholder}
      className={`pb-editable ${className}`}
      onBlur={(e) => onChange(e.currentTarget.innerText)}
    />
  );
}

// Rich (HTML) contentEditable element for the Text block.
function EditableRich({ html, onChange, edit, className = "" }) {
  if (!edit)
    return <div className={className} dangerouslySetInnerHTML={{ __html: html || "" }} />;
  const seed = (el) => {
    if (el && el.dataset.seeded !== "1") {
      el.innerHTML = html || "";
      el.dataset.seeded = "1";
    }
  };
  return (
    <div
      ref={seed}
      contentEditable
      suppressContentEditableWarning
      data-ph="Write something…"
      className={`pb-editable ${className}`}
      onBlur={(e) => onChange(e.currentTarget.innerHTML)}
    />
  );
}

// Small labelled input used for non-text props while editing (URLs, hrefs).
function MiniInput({ label, value, onChange, placeholder = "" }) {
  return (
    <label className="flex items-center gap-2 text-[11px] text-[var(--dojo-ink-soft)]">
      <span className="uppercase tracking-wider shrink-0">{label}</span>
      <input
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 min-w-0 px-2 py-1 rounded border border-[var(--dojo-border)] bg-[var(--dojo-input-bg)] text-[var(--dojo-ink)] text-xs"
      />
    </label>
  );
}

// Downscale + compress a picked image file to a JPEG data URL so it can be
// embedded directly in the page content without bloating the payload.
function fileToResizedDataURL(file, maxW = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new window.Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / (img.width || maxW));
        const w = Math.round((img.width || maxW) * scale);
        const h = Math.round((img.height || maxW) * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        try {
          resolve(canvas.toDataURL("image/jpeg", quality));
        } catch (e) {
          resolve(reader.result); // fallback: original data URL
        }
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// URL text field + an Upload button (uploads embed a resized data URL).
function ImageInput({ label = "Image URL", value, onChange, maxW = 1600, placeholder = "Paste URL or upload →" }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setBusy(true);
    try {
      const url = await fileToResizedDataURL(f, maxW);
      onChange(url);
    } catch (_) {
      /* ignore */
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <label className="flex items-center gap-2 text-[11px] text-[var(--dojo-ink-soft)]">
      <span className="uppercase tracking-wider shrink-0">{label}</span>
      <input
        value={(value || "").startsWith("data:") ? "(uploaded image)" : value || ""}
        placeholder={placeholder}
        readOnly={(value || "").startsWith("data:")}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 min-w-0 px-2 py-1 rounded border border-[var(--dojo-border)] bg-[var(--dojo-input-bg)] text-[var(--dojo-ink)] text-xs"
      />
      <span className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded border border-[var(--dojo-maroon)] text-[var(--dojo-maroon)] cursor-pointer hover:bg-[var(--dojo-maroon-soft)]">
        <Upload size={12} /> {busy ? "…" : "Upload"}
        <input type="file" accept="image/*" className="hidden" onChange={onFile} />
      </span>
    </label>
  );
}

const alignClass = (a) =>
  a === "center" ? "text-center" : a === "right" ? "text-right" : "text-left";
const justifyClass = (a) =>
  a === "center" ? "justify-center" : a === "right" ? "justify-end" : "justify-start";

/* ---------------------------------------------------------------------------
 * Individual block views (shared between read + edit modes)
 * ------------------------------------------------------------------------- */

function LinkOrA({ href, className, children }) {
  const isInternal = href && href.startsWith("/");
  if (isInternal) return <Link to={href} className={className}>{children}</Link>;
  return (
    <a href={href || "#"} className={className} target={href ? "_blank" : undefined} rel="noopener noreferrer">
      {children}
    </a>
  );
}

function HeroBlock({ p, edit, set }) {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        {p.image ? (
          <img src={p.image} alt="" referrerPolicy="no-referrer" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[var(--dojo-ink)]" />
        )}
        <div className="absolute inset-0 bg-black/55" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-28 md:py-40 text-[#FBFAF6]">
        <div className="flex items-center gap-4 mb-6">
          <span className="hinomaru-dot" />
          <EditableText
            value={p.eyebrow}
            edit={edit}
            onChange={(v) => set("eyebrow", v)}
            placeholder="Eyebrow text"
            className="text-[10px] uppercase tracking-[0.32em]"
          />
        </div>
        <EditableText
          as="h1"
          value={p.headline}
          edit={edit}
          onChange={(v) => set("headline", v)}
          placeholder="Headline"
          className="font-serif text-5xl md:text-7xl font-medium tracking-tight leading-[0.95] mb-6 max-w-4xl"
        />
        <EditableText
          as="p"
          value={p.subtext}
          edit={edit}
          onChange={(v) => set("subtext", v)}
          placeholder="Supporting text"
          className="text-lg md:text-xl max-w-2xl text-[#FBFAF6]/80 font-light leading-relaxed"
        />
        <div className="flex flex-wrap gap-4 mt-8">
          {p.primaryLabel ? (
            <LinkOrA href={p.primaryHref} className="btn-primary inline-flex items-center gap-2">
              {p.primaryLabel} <ArrowRight size={16} />
            </LinkOrA>
          ) : null}
          {p.secondaryLabel ? (
            <LinkOrA
              href={p.secondaryHref}
              className="inline-flex items-center gap-2 px-6 py-3 border border-white/70 text-white rounded"
            >
              {p.secondaryLabel}
            </LinkOrA>
          ) : null}
        </div>
        {edit && (
          <div className="mt-6 grid gap-2 max-w-xl bg-black/40 p-3 rounded">
            <ImageInput label="Hero image" value={p.image} onChange={(v) => set("image", v)} />
            <div className="grid grid-cols-2 gap-2">
              <MiniInput label="Btn 1" value={p.primaryLabel} onChange={(v) => set("primaryLabel", v)} />
              <MiniInput label="Link 1" value={p.primaryHref} onChange={(v) => set("primaryHref", v)} />
              <MiniInput label="Btn 2" value={p.secondaryLabel} onChange={(v) => set("secondaryLabel", v)} />
              <MiniInput label="Link 2" value={p.secondaryHref} onChange={(v) => set("secondaryHref", v)} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function HeadingBlock({ p, edit, set }) {
  const Tag = p.level === "h1" ? "h1" : p.level === "h3" ? "h3" : "h2";
  const sizeCls =
    p.level === "h1"
      ? "text-4xl md:text-6xl"
      : p.level === "h3"
      ? "text-xl md:text-2xl"
      : "text-3xl md:text-4xl";
  return (
    <div className={`max-w-4xl mx-auto px-6 ${alignClass(p.align)}`}>
      {(p.eyebrow || edit) && (
        <EditableText
          value={p.eyebrow}
          edit={edit}
          onChange={(v) => set("eyebrow", v)}
          placeholder="Eyebrow (optional)"
          className="text-[11px] uppercase tracking-[0.28em] text-[var(--dojo-green)] mb-2"
        />
      )}
      <EditableText
        as={Tag}
        value={p.text}
        edit={edit}
        onChange={(v) => set("text", v)}
        placeholder="Heading text"
        className={`font-serif font-medium tracking-tight text-[var(--dojo-ink)] ${sizeCls}`}
      />
      {edit && (
        <div className="mt-3 flex gap-4 justify-center text-[11px] text-[var(--dojo-ink-soft)]">
          <label className="flex items-center gap-1">
            Level
            <select value={p.level} onChange={(e) => set("level", e.target.value)} className="border border-[var(--dojo-border)] rounded px-1 py-0.5 bg-[var(--dojo-input-bg)]">
              <option value="h1">H1</option>
              <option value="h2">H2</option>
              <option value="h3">H3</option>
            </select>
          </label>
          <label className="flex items-center gap-1">
            Align
            <select value={p.align} onChange={(e) => set("align", e.target.value)} className="border border-[var(--dojo-border)] rounded px-1 py-0.5 bg-[var(--dojo-input-bg)]">
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </label>
        </div>
      )}
    </div>
  );
}

function TextBlock({ p, edit, set }) {
  return (
    <div className={`max-w-3xl mx-auto px-6 pb-prose ${alignClass(p.align)}`}>
      <EditableRich html={p.html} edit={edit} onChange={(v) => set("html", v)} />
      {edit && (
        <div className="mt-3 text-[11px] text-[var(--dojo-ink-soft)]">
          <label className="flex items-center gap-1 justify-center">
            Align
            <select value={p.align} onChange={(e) => set("align", e.target.value)} className="border border-[var(--dojo-border)] rounded px-1 py-0.5 bg-[var(--dojo-input-bg)]">
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </label>
        </div>
      )}
    </div>
  );
}

function ImageBlock({ p, edit, set }) {
  return (
    <figure className="max-w-4xl mx-auto px-6">
      {p.src ? (
        <img
          src={p.src}
          alt={p.alt || ""}
          referrerPolicy="no-referrer"
          className={`w-full object-cover ${p.rounded ? "rounded-2xl" : ""}`}
        />
      ) : (
        <div className="w-full aspect-video rounded-2xl bg-[var(--dojo-paper-alt)] border border-dashed border-[var(--dojo-border)] flex items-center justify-center text-[var(--dojo-ink-soft)] text-sm">
          No image — paste a URL below
        </div>
      )}
      {(p.caption || edit) && (
        <EditableText
          as="figcaption"
          value={p.caption}
          edit={edit}
          onChange={(v) => set("caption", v)}
          placeholder="Caption (optional)"
          className="text-center text-sm text-[var(--dojo-ink-soft)] mt-3"
        />
      )}
      {edit && (
        <div className="mt-3 grid gap-2 max-w-xl mx-auto">
          <ImageInput label="Image" value={p.src} onChange={(v) => set("src", v)} />
          <MiniInput label="Alt text" value={p.alt} onChange={(v) => set("alt", v)} />
        </div>
      )}
    </figure>
  );
}

function ButtonBlock({ p, edit, set }) {
  const cls =
    p.variant === "outline"
      ? "inline-flex items-center gap-2 px-6 py-3 border border-[var(--dojo-maroon)] text-[var(--dojo-maroon)] rounded"
      : "btn-primary inline-flex items-center gap-2";
  return (
    <div className={`max-w-4xl mx-auto px-6 flex ${justifyClass(p.align)}`}>
      <div>
        <LinkOrA href={p.href} className={cls}>
          <EditableText value={p.label} edit={edit} onChange={(v) => set("label", v)} placeholder="Button label" />
          {p.variant !== "outline" && <ArrowRight size={16} />}
        </LinkOrA>
        {edit && (
          <div className="mt-2 grid gap-2">
            <MiniInput label="Link" value={p.href} onChange={(v) => set("href", v)} placeholder="/contact or https://…" />
            <label className="flex items-center gap-2 text-[11px] text-[var(--dojo-ink-soft)]">
              <span className="uppercase tracking-wider">Style</span>
              <select value={p.variant} onChange={(e) => set("variant", e.target.value)} className="border border-[var(--dojo-border)] rounded px-1 py-0.5 bg-[var(--dojo-input-bg)] text-xs">
                <option value="primary">Primary</option>
                <option value="outline">Outline</option>
              </select>
              <span className="uppercase tracking-wider">Align</span>
              <select value={p.align} onChange={(e) => set("align", e.target.value)} className="border border-[var(--dojo-border)] rounded px-1 py-0.5 bg-[var(--dojo-input-bg)] text-xs">
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </label>
          </div>
        )}
      </div>
    </div>
  );
}

function GalleryBlock({ p, edit, set }) {
  const cols = p.columns || 3;
  const gridCls = cols === 2 ? "md:grid-cols-2" : cols === 4 ? "md:grid-cols-4" : "md:grid-cols-3";
  const images = p.images || [];
  const setImg = (i, src) => {
    const next = images.slice();
    next[i] = { ...next[i], src };
    set("images", next);
  };
  const removeImg = (i) => set("images", images.filter((_, idx) => idx !== i));
  const addImg = () => set("images", [...images, { src: "", alt: "" }]);
  const addImgUpload = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const url = await fileToResizedDataURL(f, 1600);
    set("images", [...images, { src: url, alt: "" }]);
    e.target.value = "";
  };
  return (
    <div className="max-w-6xl mx-auto px-6">
      <div className={`grid grid-cols-2 ${gridCls} gap-4`}>
        {images.map((img, i) => (
          <div key={i} className="relative group">
            {img.src ? (
              <img src={img.src} alt={img.alt || ""} referrerPolicy="no-referrer" className="w-full h-56 object-cover rounded-xl" />
            ) : (
              <div className="w-full h-56 rounded-xl bg-[var(--dojo-paper-alt)] border border-dashed border-[var(--dojo-border)]" />
            )}
            {edit && (
              <div className="mt-1 flex items-center gap-1">
                <div className="flex-1 min-w-0">
                  <ImageInput label="" value={img.src} onChange={(v) => setImg(i, v)} maxW={1200} />
                </div>
                <button type="button" onClick={() => removeImg(i)} className="text-[var(--dojo-hinomaru)] p-1" title="Remove">
                  <Trash2 size={14} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      {edit && (
        <div className="mt-4 flex items-center gap-3">
          <button type="button" onClick={addImg} className="inline-flex items-center gap-1 text-sm text-[var(--dojo-maroon)]">
            <Plus size={14} /> Add image
          </button>
          <label className="inline-flex items-center gap-1 text-sm text-[var(--dojo-maroon)] cursor-pointer">
            <Upload size={14} /> Upload image
            <input type="file" accept="image/*" className="hidden" onChange={addImgUpload} />
          </label>
          <label className="flex items-center gap-1 text-[11px] text-[var(--dojo-ink-soft)]">
            Columns
            <select value={cols} onChange={(e) => set("columns", Number(e.target.value))} className="border border-[var(--dojo-border)] rounded px-1 py-0.5 bg-[var(--dojo-input-bg)]">
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </label>
        </div>
      )}
    </div>
  );
}

function CardsBlock({ p, edit, set }) {
  const cols = p.columns || 3;
  const gridCls = cols === 2 ? "md:grid-cols-2" : cols === 4 ? "md:grid-cols-4" : "md:grid-cols-3";
  const items = p.items || [];
  const setItem = (i, key, val) => {
    const next = items.slice();
    next[i] = { ...next[i], [key]: val };
    set("items", next);
  };
  const removeItem = (i) => set("items", items.filter((_, idx) => idx !== i));
  const addItem = () => set("items", [...items, { title: "New card", body: "Description" }]);
  return (
    <div className="max-w-6xl mx-auto px-6">
      <div className={`grid grid-cols-1 ${gridCls} gap-6`}>
        {items.map((it, i) => (
          <div key={i} className="relative bg-[var(--dojo-card)] border border-[var(--dojo-border)] rounded-2xl p-6">
            {edit && (
              <button type="button" onClick={() => removeItem(i)} className="absolute top-3 right-3 text-[var(--dojo-hinomaru)]" title="Remove card">
                <Trash2 size={14} />
              </button>
            )}
            <EditableText
              as="h3"
              value={it.title}
              edit={edit}
              onChange={(v) => setItem(i, "title", v)}
              placeholder="Card title"
              className="font-serif text-xl text-[var(--dojo-ink)] mb-2"
            />
            <EditableText
              as="p"
              value={it.body}
              edit={edit}
              onChange={(v) => setItem(i, "body", v)}
              placeholder="Card body"
              className="text-sm text-[var(--dojo-ink-soft)] leading-relaxed"
            />
          </div>
        ))}
      </div>
      {edit && (
        <div className="mt-4 flex items-center gap-3">
          <button type="button" onClick={addItem} className="inline-flex items-center gap-1 text-sm text-[var(--dojo-maroon)]">
            <Plus size={14} /> Add card
          </button>
          <label className="flex items-center gap-1 text-[11px] text-[var(--dojo-ink-soft)]">
            Columns
            <select value={cols} onChange={(e) => set("columns", Number(e.target.value))} className="border border-[var(--dojo-border)] rounded px-1 py-0.5 bg-[var(--dojo-input-bg)]">
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </label>
        </div>
      )}
    </div>
  );
}

function DividerBlock() {
  return (
    <div className="max-w-4xl mx-auto px-6">
      <hr className="border-t border-[var(--dojo-border)]" />
    </div>
  );
}

function SpacerBlock({ p, edit, set }) {
  return (
    <div style={{ height: p.size || 48 }} className={edit ? "bg-[var(--dojo-green-soft)]/40 border border-dashed border-[var(--dojo-border)] flex items-center justify-center" : ""}>
      {edit && (
        <label className="flex items-center gap-1 text-[11px] text-[var(--dojo-ink-soft)]">
          Height {p.size || 48}px
          <input type="range" min={8} max={200} value={p.size || 48} onChange={(e) => set("size", Number(e.target.value))} />
        </label>
      )}
    </div>
  );
}

// Renders a single block. In edit mode `onChange(newProps)` persists changes.
export function BlockView({ block, edit = false, onChange = () => {} }) {
  const p = block.props || {};
  const set = (key, val) => onChange({ ...p, [key]: val });
  switch (block.type) {
    case "hero":
      return <HeroBlock p={p} edit={edit} set={set} />;
    case "heading":
      return <HeadingBlock p={p} edit={edit} set={set} />;
    case "text":
      return <TextBlock p={p} edit={edit} set={set} />;
    case "image":
      return <ImageBlock p={p} edit={edit} set={set} />;
    case "button":
      return <ButtonBlock p={p} edit={edit} set={set} />;
    case "gallery":
      return <GalleryBlock p={p} edit={edit} set={set} />;
    case "cards":
      return <CardsBlock p={p} edit={edit} set={set} />;
    case "divider":
      return <DividerBlock />;
    case "spacer":
      return <SpacerBlock p={p} edit={edit} set={set} />;
    default:
      return null;
  }
}

// Read-only renderer used by public pages when content.blocks exists.
export default function BlockRenderer({ blocks = [] }) {
  return (
    <div className="py-4">
      {blocks.map((b) => (
        <div key={b.id} className="py-8">
          <BlockView block={b} />
        </div>
      ))}
    </div>
  );
}
